/**
 * Plain-English rendering for cross-field rules (v0.4 Stage E).
 *
 * Both the CPA rule-confirmation card and the validator threshold
 * card route through this helper to produce a one-sentence human
 * description of a rule. The card title shows the result of
 * `renderRuleDescription(rule)`; the operator picker on the modify
 * dropdown updates this live so the reviewer always sees the rule
 * they're about to confirm.
 *
 * Per Stage E acceptance criteria, every v0.4 operator has a sensible
 * rendering — see `__tests__/ruleText.test.ts` for the full corpus.
 */
import { isSumOperator, type CrossFieldRule, type RuleOperator } from './types';

/**
 * Map an operator to the "between two columns" infix copy. Used as a
 * fallback when no rule.description is provided. Tuned for natural
 * English in the common case (`>=` reads as "must be on or after",
 * not "must be greater than or equal to") because reviewers parse
 * relationships faster than maths.
 */
const OPERATOR_PHRASE: Record<
  Exclude<RuleOperator, 'not_null_when' | 'sum_eq' | 'sum_gt' | 'sum_gte' | 'sum_lt' | 'sum_lte'>,
  string
> = {
  gt: 'must be after',
  gte: 'must be on or after',
  lt: 'must be before',
  lte: 'must be on or before',
  ne: 'must differ from',
};

/**
 * v0.5 Stage E.2 — relational phrase for summation operators.
 * The reviewer reads ``total_amount must equal the sum of <parts>``
 * rather than ``total_amount sum_eq sum(parts)``.
 */
const SUM_OPERATOR_PHRASE: Record<
  'sum_eq' | 'sum_gt' | 'sum_gte' | 'sum_lt' | 'sum_lte',
  string
> = {
  sum_eq: 'must equal the sum of',
  sum_gt: 'must be greater than the sum of',
  sum_gte: 'must be greater than or equal to the sum of',
  sum_lt: 'must be less than the sum of',
  sum_lte: 'must be less than or equal to the sum of',
};

/**
 * Map an operator to a glyph for the two-column summary block on the
 * card body. Kept short and unambiguous; the plain-English form lives
 * in the description above the glyph. Sum operators reuse the
 * pairwise-comparison glyphs since the reviewer also sees the
 * parts-list block alongside.
 */
const OPERATOR_GLYPH: Record<RuleOperator, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  ne: '≠',
  not_null_when: '⇐',
  sum_eq: '=',
  sum_gt: '>',
  sum_gte: '≥',
  sum_lt: '<',
  sum_lte: '≤',
};

/**
 * Plain-English label suitable for the operator dropdown on the
 * modify path. Each entry is the dropdown's display string; the
 * `value` is the underlying RuleOperator. Sum operators are listed
 * after the pairwise set so the reviewer's most common picks
 * (gte/gt/lte/lt/ne) stay at the top of the dropdown.
 */
export const OPERATOR_OPTIONS: Array<{ value: RuleOperator; label: string }> = [
  { value: 'gte', label: '≥  (must be on or after)' },
  { value: 'gt', label: '>  (must be after)' },
  { value: 'lte', label: '≤  (must be on or before)' },
  { value: 'lt', label: '<  (must be before)' },
  { value: 'ne', label: '≠  (must differ from)' },
  {
    value: 'not_null_when',
    label: '⇐  (must not be null when condition met)',
  },
  { value: 'sum_eq', label: 'Σ=  (must equal the sum of parts)' },
  { value: 'sum_gt', label: 'Σ>  (must be greater than the sum of parts)' },
  { value: 'sum_gte', label: 'Σ≥  (must be greater than or equal to sum)' },
  { value: 'sum_lt', label: 'Σ<  (must be less than the sum of parts)' },
  { value: 'sum_lte', label: 'Σ≤  (must be less than or equal to sum)' },
];

/**
 * Render a one-sentence English description of a rule.
 *
 * Order of preference:
 *   1. `rule.description` if provided — the CPA / validator emit one
 *      already, and reviewer overrides during a `modify_rule` action
 *      should land in this field.
 *   2. Synthesised from `{col_a, col_b, operator, condition}`.
 *
 * `not_null_when` produces a different sentence shape because it has
 * a condition rather than a binary comparison.
 */
export function renderRuleDescription(rule: CrossFieldRule): string {
  if (rule.description && rule.description.trim().length > 0) {
    return rule.description;
  }
  if (rule.operator === 'not_null_when') {
    const cond = rule.condition;
    if (cond) {
      return `${rule.col_a} must not be null when ${cond.col} = ${formatValue(cond.value)}`;
    }
    return `${rule.col_a} must not be null when the condition is met`;
  }
  if (isSumOperator(rule.operator)) {
    const phrase = SUM_OPERATOR_PHRASE[rule.operator as keyof typeof SUM_OPERATOR_PHRASE];
    const parts = rule.sum_columns ?? [];
    const partsDisplay = parts.length > 0 ? parts.join(' + ') : '(no parts selected)';
    const tolerance = rule.tolerance ?? 0.01;
    return `${rule.col_a} ${phrase} ${partsDisplay} (±${formatTolerance(tolerance)})`;
  }
  const phrase =
    OPERATOR_PHRASE[rule.operator as keyof typeof OPERATOR_PHRASE] ??
    'must relate to';
  return `${rule.col_a} ${phrase} ${rule.col_b ?? ''}`;
}

/**
 * v0.5 Stage E.2 — render a tolerance value with two decimal places
 * for currency-style display. Avoids locale-specific currency symbols
 * since the reviewer's locale is not available at this layer.
 */
function formatTolerance(tolerance: number): string {
  return tolerance.toFixed(2);
}

/**
 * Glyph for the operator. Used in the card body's two-column summary.
 */
export function operatorGlyph(operator: RuleOperator): string {
  return OPERATOR_GLYPH[operator] ?? '?';
}

/**
 * Format a condition value for display. Strings are quoted; numbers
 * are shown as-is.
 */
function formatValue(value: string | number): string {
  if (typeof value === 'number') {
    return String(value);
  }
  return `"${value}"`;
}
